import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle, faDownload, faListCheck, faPlus, faTimes, faTimesCircle,
} from '@fortawesome/pro-light-svg-icons'

import { Box, Button } from '@domain-group/suit-theme'

import handleCallbackAction from '@functions/handleCallbackAction'
import { confirmAction } from '@functions/entity'

import useCurrentUser from '@hooks/useCurrentUser'

import type { UseMediaReviewerOrderItemPayload } from '../../hooks/useMediaReviewerOrderItem'

const downloadQueued = () => {
  // @ts-expect-error TS2769
  swal.fire({
    title: 'Download Queued!',
    text: 'Please stay on this page while we package the items. This could take a few minutes.',
    icon: 'success',
    animation: 'slide-from-top',
  })
}

type ToggleSelectModeParams = {
  bulkSelectMode: UseMediaReviewerOrderItemPayload['bulkSelectMode'],
  deselectAllEntityIds: UseMediaReviewerOrderItemPayload['callbacks']['deselectAllEntityIds'],
  setModalState: UseMediaReviewerOrderItemPayload['callbacks']['setModalState'],
}

const toggleSelectMode = (params: ToggleSelectModeParams) => {
  const { bulkSelectMode, deselectAllEntityIds, setModalState } = params

  deselectAllEntityIds()

  setModalState({
    bulkSelectMode: !bulkSelectMode,
    commentMode: false,
    // @ts-expect-error TS2353
    selectedAttachmentId: undefined,
  })
}

type OrderItemBulkActionsProps = {
  orderItemPayload: UseMediaReviewerOrderItemPayload,
}

const OrderItemBulkActions = (props: OrderItemBulkActionsProps) => {
  const { orderItemPayload } = props

  const {
    allSelected,
    bulkSelectMode,
    callbacks: {
      copyToCampaign,
      deselectAllEntityIds,
      downloadAttachments: downloadFn,
      selectAllEntityIds,
      setModalState,
    },
    hasBulkSelectedIds,
    selectedEntityIds,
  } = orderItemPayload

  const currentUserPayload = useCurrentUser()
  const { isProviderUser } = currentUserPayload

  const confirmActionPayload = {
    callbacks: {
      actionFn: copyToCampaign,
    },
    options: {
      entityParams: selectedEntityIds,
      confirmButtonColor: '#43ac6a',
      icon: 'success',
    },
    strings: {
      actionString: 'Copy to',
      entityName: 'campaign',
      confirmString: 'Are you sure? It can take up to 5 minutes for images to be processed.',
      toastText: 'Copied Media to Campaign',
    },
  }

  const downloadAttachmentsPayload = {
    callbacks: {
      action: downloadFn,
      afterAction: downloadQueued,
    },
  }

  return (
    <Box
      borderTop="1px solid"
      borderColor="lineColor"
      flexDirection={['column', 'row']}
      padding="large"
    >
      <Button
        buttonStyle="secondaryUtility"
        icon={<FontAwesomeIcon icon={bulkSelectMode ? faTimes : faListCheck} />}
        marginRight={[0, 'medium']}
        marginTop={['large', 0]}
        onClick={() => toggleSelectMode({ bulkSelectMode, deselectAllEntityIds, setModalState })}
        size="medium"
        width="auto"
      >
        {bulkSelectMode ? 'Exit Select Mode' : 'Bulk Select Media' }
      </Button>

      {bulkSelectMode && (
        <>
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={allSelected ? faTimesCircle : faCheckCircle} />}
            marginRight={[0, 'medium']}
            marginTop={['large', 0]}
            onClick={allSelected
              ? () => deselectAllEntityIds()
              : () => selectAllEntityIds()}
            size="medium"
            width="auto"
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </Button>

          {hasBulkSelectedIds && !isProviderUser && (
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faPlus} />}
              marginRight={[0, 'medium']}
              marginTop={['large', 0]}
              // @ts-expect-error TS2345
              onClick={() => confirmAction(confirmActionPayload)}
              size="medium"
              width="auto"
            >
              Add Selected to Campaign
            </Button>
          )}

          {hasBulkSelectedIds && (
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faDownload} />}
              marginTop={['large', 0]}
              // @ts-expect-error TS2345
              onClick={() => handleCallbackAction(downloadAttachmentsPayload)}
              size="medium"
              width="auto"
            >
              Download
            </Button>
          )}
        </>
      )}
    </Box>
  )
}

export default OrderItemBulkActions
